/*!
 * spectrumBands.js — where a sound's energy is: low, mid, high, and how bright
 * © 2026 Rob Canning — GPLv3
 *
 *   <name>.low       its share below 250 Hz, 0..1 -- the bass in it
 *   <name>.mid       250 Hz .. 2 kHz
 *   <name>.high      above 2 kHz
 *   <name>.centroid  its brightness, 0..1: the spectrum's centre, 40 Hz .. 8 kHz
 *                    on a log scale (as pitch is heard)
 *
 * Shares of the sound's own energy, so they add up to 1 whatever its level:
 * a fuzz that flattens the loudness does not flatten them. (The adc()'s
 * low / mid / high are levels of the input, its centroid is the same idea.)
 * Pure: the voices' analysers are read in liveSignals.js, summed here.
 */

const LOW_HZ = 250, HIGH_HZ = 2000, C_LO = 40, C_HI = 8000;

/** A new sum, for the voices of one sound. */
export const emptyBands = () => ({ low: 0, mid: 0, high: 0, weighted: 0 });

/** Add one analyser's spectrum (getFloatFrequencyData: dB per bin) to `acc`. */
export function addSpectrum(acc, db, sampleRate) {
  const binHz = sampleRate / 2 / db.length;
  for (let i = 1; i < db.length; i++) {
    const v = db[i];
    if (!Number.isFinite(v)) continue;
    const p = 10 ** (v / 10), f = i * binHz;       // power, and where
    if (f < LOW_HZ) acc.low += p; else if (f < HIGH_HZ) acc.mid += p; else acc.high += p;
    acc.weighted += p * f;
  }
  return acc;
}

/** The shares and the brightness of a sum, or null when it is silent. */
export function bandShares(acc) {
  const total = acc.low + acc.mid + acc.high;
  if (!(total > 0)) return null;
  const hz = acc.weighted / total;
  const centroid = Math.min(1, Math.max(0, Math.log(Math.max(hz, C_LO) / C_LO) / Math.log(C_HI / C_LO)));
  return { low: acc.low / total, mid: acc.mid / total, high: acc.high / total, centroid };
}

// ---- band(k, n): the spectrum in n parts ---------------------------------
//
//   follow(bass.band(1, 4), lo, hi)   the lowest of 4 bands; band(4, 4) the highest
//   follow(adc.band(3, 8), lo, hi)    the input's third of 8
//
// The bands are cut on a log scale (as pitch is heard), 30 Hz .. 16 kHz, so
// each holds as many octaves as the next. Each is its power against its own
// loudest lately (forgetting over ~10 s) -- every band reaches 1, the treble
// as the bass, which a share of the whole would not let it -- rising at once
// and falling over ~0.2 s. Made only while something follows one: the
// channel band2of4 (parser.js reads band(2, 4) as it). Both the sounds
// (liveSignals.js) and the external input (audioAnalysis.js) give them.

const B_LO = 30, B_HI = 16000;
const B_FLOOR = 1e-7;           // below this a band is silent: noise is not blown up to 1

/** The band counts followed under `prefix` ("bass."): { n: Set of k }, from paths like bass.band2of4. */
export function bandsWanted(paths, prefix) {
  const out = {};
  for (const p of paths) {
    const m = new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}band(\\d+)of(\\d+)$`).exec(p);
    if (!m) continue;
    const k = Number(m[1]), n = Number(m[2]);
    if (n >= 1 && n <= 64 && k >= 1 && k <= n) (out[n] ||= new Set()).add(k);
  }
  return out;
}

/** Add one spectrum (dB per bin) into `acc`, n power sums low to high. */
export function addBandPowers(acc, db, sampleRate, n) {
  const binHz = sampleRate / 2 / db.length;
  const span = Math.log(B_HI / B_LO);
  for (let i = 1; i < db.length; i++) {
    const v = db[i], f = i * binHz;
    if (!Number.isFinite(v) || f < B_LO || f >= B_HI) continue;
    const b = Math.min(n - 1, Math.floor(n * Math.log(f / B_LO) / span));
    acc[b] += 10 ** (v / 10);
  }
  return acc;
}

/**
 * The bands' levels, 0..1, from their power sums: each against its own
 * loudest lately (state.peaks, forgetting by `fall` a call), falling by
 * `release` a call. Returns { band1of4: .., band2of4: .. } for the k wanted.
 */
export function bandLevels(acc, n, wantedK, state, { fall = 0.997, release = 0.85 } = {}) {
  const peaks = state.peaks ||= new Float64Array(n), vals = state.vals ||= new Float64Array(n);
  const out = {};
  for (let b = 0; b < n; b++) {
    const p = acc[b] > B_FLOOR ? acc[b] : 0;
    peaks[b] = Math.max(p, peaks[b] * fall, B_FLOOR * 10);
    const v = Math.sqrt(p / peaks[b]);            // as amplitude: a band at a quarter power is at half
    vals[b] = Math.max(v, vals[b] * release);
    if (wantedK.has(b + 1)) out[`band${b + 1}of${n}`] = Math.min(1, vals[b]);
  }
  return out;
}
